import { useState, useRef, useEffect, useCallback } from "react";
import axios from "axios";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Slider } from "@/components/ui/slider";
import { Switch } from "@/components/ui/switch";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Images, ChevronLeft, ChevronRight, X, Play, Pause, Download, Loader2, XCircle, UploadCloud, Film, FolderOpen, Plus, Trash2, ArrowLeft, Check } from "lucide-react";
import { toast } from "sonner";
import { chunkedUpload, downloadFile, API } from "./upload";

const fmtTime = (s) => {
  s = Math.max(0, s || 0);
  return `${Math.floor(s / 60)}:${String(Math.floor(s % 60)).padStart(2, "0")}`;
};
const ago = (ts) => {
  const s = Math.max(0, Date.now() / 1000 - ts);
  if (s < 60) return "just now";
  if (s < 3600) return `${Math.floor(s / 60)}m ago`;
  if (s < 86400) return `${Math.floor(s / 3600)}h ago`;
  return `${Math.floor(s / 86400)}d ago`;
};

const ASPECTS = [
  { id: "16:9", label: "16:9 · Landscape", testId: "album-aspect-169" },
  { id: "1:1", label: "1:1 · Square", testId: "album-aspect-11" },
  { id: "9:16", label: "9:16 · Vertical", testId: "album-aspect-916" },
];
const TRANSITIONS = [
  { id: "none", label: "None", testId: "album-transition-none" },
  { id: "fade", label: "Crossfade", testId: "album-transition-fade" },
  { id: "slideleft", label: "Slide ←", testId: "album-transition-slideleft" },
  { id: "slideright", label: "Slide →", testId: "album-transition-slideright" },
  { id: "circleopen", label: "Circle", testId: "album-transition-circleopen" },
];
const DEFAULT_SETTINGS = {
  per_photo_seconds: 3, aspect: "16:9", resolution: "1080",
  transition: "fade", transition_seconds: 0.6,
  music_volume: 0.8, fade_in: 1, fade_out: 2, loop_music: true, music_offset: 0,
};
const isVideoFile = (name) => /\.(mp4|mov|webm|m4v)$/i.test(name || "");

// Dialog used both for list-delete and the post-export cleanup prompt.
const CleanupDialog = ({ open, onClose, albumName, onKeep, onDeleteAlbum, onDeleteAll, title, description }) => (
  <Dialog open={open} onOpenChange={(v) => { if (!v) onClose(); }}>
    <DialogContent className="max-w-md" data-testid="album-cleanup-dialog">
      <DialogHeader>
        <DialogTitle className="font-mono text-sm">{title}</DialogTitle>
        <DialogDescription className="font-mono text-xs">{description}</DialogDescription>
      </DialogHeader>
      <div className="space-y-2">
        <Button className="w-full h-9 font-mono text-xs justify-start" variant="outline" onClick={onKeep} data-testid="cleanup-keep">
          <Check className="w-4 h-4 mr-2 text-emerald-400" /> Keep everything — I may edit "{albumName}" again
        </Button>
        <Button className="w-full h-9 font-mono text-xs justify-start" variant="outline" onClick={onDeleteAlbum} data-testid="cleanup-album-only">
          <Trash2 className="w-4 h-4 mr-2 text-amber-400" /> Delete album, keep the photos &amp; videos
        </Button>
        <Button className="w-full h-9 font-mono text-xs justify-start" variant="outline" onClick={onDeleteAll} data-testid="cleanup-album-media">
          <Trash2 className="w-4 h-4 mr-2 text-rose-400" /> Delete album AND its photos &amp; videos
        </Button>
        <p className="font-mono text-[10px] text-muted-foreground">Your uploaded music is always kept for future projects.</p>
      </div>
    </DialogContent>
  </Dialog>
);

// ======================= Album list =======================
const AlbumList = ({ onOpen }) => {
  const [albums, setAlbums] = useState(null);
  const [newName, setNewName] = useState("");
  const [deleting, setDeleting] = useState(null); // album to delete

  const load = useCallback(() => {
    axios.get(`${API}/funtime/albums`).then((r) => setAlbums(r.data.albums || [])).catch(() => setAlbums([]));
  }, []);
  useEffect(() => { load(); }, [load]);

  const create = async () => {
    try {
      const res = await axios.post(`${API}/funtime/albums`, { name: newName.trim() || `Album ${(albums?.length || 0) + 1}` });
      setNewName("");
      onOpen(res.data);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not create album");
    }
  };

  const doDelete = async (deleteMedia) => {
    const alb = deleting;
    setDeleting(null);
    try {
      await axios.delete(`${API}/funtime/albums/${alb.album_id}?delete_media=${deleteMedia}`);
      toast.success(deleteMedia ? "Album and its media deleted" : "Album deleted — media kept");
      load();
    } catch {
      toast.error("Could not delete album");
    }
  };

  return (
    <Card className="border-border/60">
      <CardContent className="p-4 space-y-3" data-testid="album-list">
        <p className="font-mono text-xs font-bold text-primary">My Albums</p>
        <div className="flex items-center gap-2">
          <Input value={newName} onChange={(e) => setNewName(e.target.value)} placeholder="New album name…"
            className="h-8 font-mono text-xs max-w-xs" data-testid="album-new-name" />
          <Button size="sm" className="btn-3d h-8 font-mono text-xs" onClick={create} data-testid="album-create-btn">
            <Plus className="w-3.5 h-3.5 mr-1" /> New album
          </Button>
        </div>
        {albums === null ? (
          <p className="font-mono text-[11px] text-muted-foreground">Loading…</p>
        ) : albums.length === 0 ? (
          <p className="font-mono text-[11px] text-muted-foreground py-4" data-testid="album-list-empty">
            No albums yet — create one, then add photos and video clips to it.
          </p>
        ) : (
          <ul className="space-y-2">
            {albums.map((a, i) => (
              <li key={a.album_id} className="flex items-center gap-3 rounded-lg border border-border/60 bg-secondary/30 px-3 py-2" data-testid={`album-row-${i}`}>
                <div className="w-14 h-10 rounded bg-black overflow-hidden flex-shrink-0 flex items-center justify-center">
                  {a.cover ? (
                    a.cover.type === "video"
                      ? <video src={`${API}/funtime/file/${a.cover.file_id}`} muted preload="metadata" className="w-full h-full object-cover" />
                      : <img src={`${API}/funtime/file/${a.cover.file_id}`} alt="" className="w-full h-full object-cover" loading="lazy" />
                  ) : <Images className="w-4 h-4 text-muted-foreground" />}
                </div>
                <div className="min-w-0 flex-1 font-mono">
                  <p className="text-xs font-bold text-card-foreground truncate">{a.name}</p>
                  <p className="text-[10px] text-muted-foreground">{a.item_count} item{a.item_count === 1 ? "" : "s"} · updated {ago(a.updated_ts)}</p>
                </div>
                <Button size="sm" className="btn-3d h-7 font-mono text-[10px]" onClick={() => onOpen(a)} data-testid={`album-open-${i}`}>
                  <FolderOpen className="w-3.5 h-3.5 mr-1" /> Open
                </Button>
                <Button size="icon" variant="ghost" className="h-7 w-7 text-rose-400 hover:text-rose-300" onClick={() => setDeleting(a)} title="Delete album" data-testid={`album-list-delete-${i}`}>
                  <Trash2 className="w-3.5 h-3.5" />
                </Button>
              </li>
            ))}
          </ul>
        )}
        <CleanupDialog
          open={!!deleting} onClose={() => setDeleting(null)} albumName={deleting?.name || ""}
          title={`Delete "${deleting?.name}"?`} description="Choose what to remove."
          onKeep={() => setDeleting(null)}
          onDeleteAlbum={() => doDelete(false)}
          onDeleteAll={() => doDelete(true)}
        />
      </CardContent>
    </Card>
  );
};

// ======================= Album editor =======================
export default function PhotoAlbumMaker({ music, musicUrl, musicSlot }) {
  const [album, setAlbum] = useState(null);       // {album_id, name}
  const [items, setItems] = useState([]);         // [{file_id, type, filename, duration}]
  const [name, setName] = useState("");
  const [settings, setSettings] = useState(DEFAULT_SETTINGS);
  const [loadedTs, setLoadedTs] = useState(0);    // guards autosave until album loaded
  const [saveState, setSaveState] = useState("saved"); // saved | saving
  const [uploading, setUploading] = useState(null);
  const [outputName, setOutputName] = useState("album");
  const [job, setJob] = useState(null);
  const [playing, setPlaying] = useState(false);
  const [t, setT] = useState(0);
  const [dragOver, setDragOver] = useState(false);
  const [cleanupOpen, setCleanupOpen] = useState(false);
  const [downloading, setDownloading] = useState(false);
  const musicRef = useRef(null);
  const vidRef = useRef(null);
  const pollRef = useRef(null);
  const tickRef = useRef(null);
  const saveRef = useRef(null);

  // ---- timeline math (mixed photos + videos) ----
  const n = items.length;
  const per = settings.per_photo_seconds;
  const itemDur = (it) => (it.type === "video" ? Math.max(0.5, it.duration || 0.5) : per);
  const durs = items.map(itemDur);
  const minDur = durs.length ? Math.min(...durs) : per;
  const trans = settings.transition !== "none" && n >= 2 ? Math.min(settings.transition_seconds, minDur * 0.75) : 0;
  const starts = [];
  let acc = 0;
  durs.forEach((d, i) => { starts.push(acc - i * trans); acc += d; });
  const totalDur = n ? acc - (n - 1) * trans : 0;
  let idx = 0;
  for (let i = 0; i < starts.length; i++) if (t >= starts[i]) idx = i;
  const inTrans = trans > 0 && idx > 0 && t - starts[idx] < trans;
  const tp = inTrans ? (t - starts[idx]) / trans : 1;

  useEffect(() => () => {
    if (pollRef.current) clearInterval(pollRef.current);
    if (tickRef.current) clearInterval(tickRef.current);
    if (saveRef.current) clearTimeout(saveRef.current);
    if (musicRef.current) musicRef.current.pause();
  }, []);

  // ---- open / autosave ----
  const openAlbum = async (a) => {
    try {
      const res = await axios.get(`${API}/funtime/albums/${a.album_id}`);
      const alb = res.data;
      setAlbum({ album_id: alb.album_id });
      setItems(alb.items || []);
      setName(alb.name);
      setSettings({ ...DEFAULT_SETTINGS, ...(alb.settings || {}) });
      setOutputName((alb.name || "album").toLowerCase().replace(/[^a-z0-9 _-]/g, "").trim() || "album");
      setJob(null);
      setT(0);
      setLoadedTs(Date.now());
    } catch {
      toast.error("Could not open album");
    }
  };
  const closeAlbum = () => {
    if (tickRef.current) clearInterval(tickRef.current);
    if (musicRef.current) musicRef.current.pause();
    setPlaying(false);
    setAlbum(null);
    setLoadedTs(0);
  };

  useEffect(() => {
    if (!album || !loadedTs) return;
    setSaveState("saving");
    if (saveRef.current) clearTimeout(saveRef.current);
    saveRef.current = setTimeout(async () => {
      try {
        await axios.put(`${API}/funtime/albums/${album.album_id}`, { name, items, settings });
        setSaveState("saved");
      } catch {
        setSaveState("error");
      }
    }, 800);
  }, [items, settings, name]); // eslint-disable-line react-hooks/exhaustive-deps

  // ---- uploads (photos AND video clips) ----
  const addFiles = async (fileList) => {
    const files = Array.from(fileList || []).slice(0, 40 - items.length);
    if (!files.length) return;
    setUploading({ done: 0, total: files.length });
    const added = [];
    for (let i = 0; i < files.length; i++) {
      const kind = isVideoFile(files[i].name) ? "video" : "image";
      try {
        const meta = await chunkedUpload(files[i], kind);
        added.push({ file_id: meta.file_id, type: kind, filename: meta.filename, duration: meta.duration || 0 });
      } catch (err) {
        toast.error(`${files[i].name}: ${err.response?.data?.detail || "upload failed"}`);
      }
      setUploading({ done: i + 1, total: files.length });
    }
    setItems((p) => [...p, ...added]);
    setUploading(null);
    if (added.length) toast.success(`${added.length} item${added.length > 1 ? "s" : ""} added to the album`);
  };

  const move = (i, dir) => setItems((p) => {
    const j = i + dir;
    if (j < 0 || j >= p.length) return p;
    const next = [...p];
    [next[i], next[j]] = [next[j], next[i]];
    return next;
  });
  const removeItem = (i) => setItems((p) => p.filter((_, k) => k !== i));

  // ---- preview ----
  const syncMusic = useCallback((time, isPlaying) => {
    const m = musicRef.current;
    if (!m || !music) return;
    const s = settings;
    const rel = time - s.music_offset;
    const segLen = Math.max(0.1, music.duration);
    const used = Math.max(0.1, totalDur - s.music_offset);
    const active = rel >= 0 && (s.loop_music ? rel < used : rel < Math.min(segLen, used));
    if (!active || !isPlaying) { if (!m.paused) m.pause(); return; }
    const pos = s.loop_music ? rel % segLen : rel;
    if (Math.abs(m.currentTime - pos) > 0.35) m.currentTime = pos;
    let fade = 1;
    const totalUsed = s.loop_music ? used : Math.min(segLen, used);
    if (s.fade_in > 0 && rel < s.fade_in) fade = rel / s.fade_in;
    if (s.fade_out > 0 && rel > totalUsed - s.fade_out) fade = Math.min(fade, Math.max(0, (totalUsed - rel) / s.fade_out));
    m.volume = Math.min(1, Math.max(0, s.music_volume * fade));
    if (m.paused) m.play().catch(() => {});
  }, [music, settings, totalDur]);

  // keep the preview <video> element in step with the clock
  useEffect(() => {
    const v = vidRef.current;
    const it = items[idx];
    if (!v || !it || it.type !== "video") return;
    const local = Math.max(0, t - starts[idx]);
    if (Math.abs(v.currentTime - local) > 0.45) v.currentTime = local;
    if (playing && v.paused) v.play().catch(() => {});
    if (!playing && !v.paused) v.pause();
  });

  const togglePlay = () => {
    if (!n) return;
    if (playing) {
      setPlaying(false);
      if (tickRef.current) clearInterval(tickRef.current);
      if (musicRef.current) musicRef.current.pause();
      return;
    }
    setPlaying(true);
    let cur = t >= totalDur - 0.15 ? 0 : t;
    setT(cur);
    tickRef.current = setInterval(() => {
      cur += 0.1;
      if (cur >= totalDur) {
        clearInterval(tickRef.current);
        setPlaying(false);
        if (musicRef.current) musicRef.current.pause();
        setT(0);
        return;
      }
      setT(cur);
      syncMusic(cur, true);
    }, 100);
  };

  // ---- export ----
  const startExport = async () => {
    try {
      const res = await axios.post(`${API}/funtime/export`, {
        mode: "slideshow",
        item_file_ids: items.map((p) => p.file_id),
        music: music ? { source: music.source, file_id: music.file_id, track_id: music.track_id } : { source: "none" },
        settings: { ...settings, music_start: 0, music_end: music?.duration || 0 },
        output_name: outputName,
      });
      const jobId = res.data.job_id;
      setJob({ id: jobId, status: "processing", progress: 0 });
      pollRef.current = setInterval(async () => {
        try {
          const st = await axios.get(`${API}/funtime/export/${jobId}/status`);
          setJob({ id: jobId, ...st.data });
          if (["done", "error", "cancelled"].includes(st.data.status)) {
            clearInterval(pollRef.current);
            if (st.data.status === "done") toast.success("Album exported — ready to download!");
            if (st.data.status === "error") toast.error("Export failed — adjust and retry");
          }
        } catch { /* transient */ }
      }, 1000);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not start export");
    }
  };
  const cancelExport = async () => {
    if (!job) return;
    try { await axios.post(`${API}/funtime/export/${job.id}/cancel`); } catch { /* ignore */ }
    setJob(null);
    if (pollRef.current) clearInterval(pollRef.current);
  };
  const downloadExport = async () => {
    setDownloading(true);
    try {
      await downloadFile(`${API}/funtime/export/${job.id}/download`, `${outputName || "album"}.mp4`);
      setCleanupOpen(true); // ask what to do with the album + media
    } catch {
      toast.error("Download failed — please try again");
    } finally {
      setDownloading(false);
    }
  };
  const cleanupDelete = async (deleteMedia) => {
    setCleanupOpen(false);
    try {
      await axios.delete(`${API}/funtime/albums/${album.album_id}?delete_media=${deleteMedia}`);
      toast.success(deleteMedia ? "Album and its photos/videos deleted" : "Album deleted — photos/videos kept");
      closeAlbum();
    } catch {
      toast.error("Cleanup failed — the album is still there");
    }
  };

  const set = (k) => (v) => setSettings((p) => ({ ...p, [k]: Array.isArray(v) ? v[0] : v }));
  const canExport = n > 0 && !uploading && (!job || ["done", "error", "cancelled"].includes(job.status));
  const aspectCss = { "16:9": "16 / 9", "1:1": "1 / 1", "9:16": "9 / 16" }[settings.aspect];
  const hasVideos = items.some((i) => i.type === "video");

  const SliderRow = ({ label, value, min, max, step, onChange, fmt = (x) => x, testId }) => (
    <div className="space-y-1">
      <div className="flex justify-between font-mono text-[10px] uppercase text-muted-foreground">
        <span>{label}</span><span className="text-card-foreground">{fmt(value)}</span>
      </div>
      <Slider value={[value]} min={min} max={max} step={step} onValueChange={onChange} data-testid={testId} />
    </div>
  );

  // ---------- album list view ----------
  if (!album) {
    return (
      <div className="space-y-4" data-testid="photo-album-maker">
        <AlbumList onOpen={openAlbum} />
      </div>
    );
  }

  // ---------- editor view ----------
  const cur = items[idx];
  const prevItem = inTrans ? items[idx - 1] : null;
  const transStyle = !inTrans ? undefined
    : settings.transition === "slideleft" ? { transform: `translateX(${((1 - tp) * 100).toFixed(1)}%)` }
    : settings.transition === "slideright" ? { transform: `translateX(${(-(1 - tp) * 100).toFixed(1)}%)` }
    : { opacity: tp };

  return (
    <div className="space-y-4" data-testid="photo-album-maker">
      <div className="flex flex-wrap items-center gap-2">
        <Button size="sm" variant="outline" className="h-8 font-mono text-xs" onClick={closeAlbum} data-testid="album-back-btn">
          <ArrowLeft className="w-3.5 h-3.5 mr-1" /> All albums
        </Button>
        <Input value={name} onChange={(e) => setName(e.target.value)} className="h-8 font-mono text-xs font-bold max-w-[220px]" data-testid="album-name-input" />
        <span className={`font-mono text-[10px] ${saveState === "error" ? "text-rose-400" : "text-muted-foreground"}`} data-testid="album-save-state">
          {saveState === "saving" ? "Saving…" : saveState === "error" ? "Save failed — will retry on next change" : "Saved"}
        </span>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        {/* STEP 1 — media + timeline */}
        <Card className="border-border/60">
          <CardContent className="p-4 space-y-3">
            <p className="font-mono text-xs font-bold text-primary">1 · Add photos &amp; videos ({n}/40)</p>
            <label
              onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
              onDragLeave={() => setDragOver(false)}
              onDrop={(e) => { e.preventDefault(); setDragOver(false); addFiles(e.dataTransfer.files); }}
              className={`flex flex-col items-center justify-center gap-2 rounded-xl border-2 border-dashed px-4 py-6 cursor-pointer transition-colors ${dragOver ? "border-primary bg-primary/10" : "border-border/70 hover:border-primary/60"}`}
              data-testid="album-dropzone"
            >
              <UploadCloud className="w-6 h-6 text-primary" />
              <span className="font-mono text-xs text-card-foreground">Drop photos or video clips, or tap to choose (multiple)</span>
              <span className="font-mono text-[10px] text-muted-foreground">JPG · PNG · WebP · MP4 · MOV · WebM</span>
              <input type="file" multiple
                accept="image/jpeg,image/png,image/webp,video/mp4,video/quicktime,video/webm,.jpg,.jpeg,.png,.webp,.mp4,.mov,.webm,.m4v"
                className="hidden" onChange={(e) => { addFiles(e.target.files); e.target.value = ""; }} data-testid="album-file-input" />
            </label>
            {uploading && (
              <p className="font-mono text-[10px] text-sky-300" data-testid="album-upload-progress">Uploading {uploading.done}/{uploading.total}…</p>
            )}
            {n > 0 && (
              <>
                <p className="font-mono text-[10px] uppercase text-muted-foreground">Timeline — arrows move an item earlier or later</p>
                <div className="grid grid-cols-3 sm:grid-cols-4 gap-2 max-h-64 overflow-y-auto pr-1" data-testid="album-timeline">
                  {items.map((p, i) => (
                    <div key={p.file_id} className={`relative rounded-lg overflow-hidden border ${i === idx && playing ? "border-primary" : "border-border/60"}`} data-testid={`album-photo-${i}`}>
                      {p.type === "video" ? (
                        <video src={`${API}/funtime/file/${p.file_id}`} muted preload="metadata" className="w-full h-16 object-cover" />
                      ) : (
                        <img src={`${API}/funtime/file/${p.file_id}`} alt={p.filename} className="w-full h-16 object-cover" loading="lazy" />
                      )}
                      <span className="absolute top-0.5 left-1 font-mono text-[9px] font-bold text-white bg-black/60 px-1 rounded">{i + 1}</span>
                      {p.type === "video" && (
                        <span className="absolute top-0.5 left-6 flex items-center gap-0.5 font-mono text-[8px] text-white bg-black/60 px-1 rounded"><Film className="w-2.5 h-2.5" />{fmtTime(p.duration)}</span>
                      )}
                      <button onClick={() => removeItem(i)} className="absolute top-0.5 right-0.5 p-0.5 rounded bg-black/60 text-rose-400" title="Remove" data-testid={`album-remove-${i}`}><X className="w-3 h-3" /></button>
                      <div className="absolute bottom-0 inset-x-0 flex justify-between bg-black/60">
                        <button onClick={() => move(i, -1)} disabled={i === 0} className="p-0.5 text-white disabled:opacity-30" title="Move earlier" data-testid={`album-move-left-${i}`}><ChevronLeft className="w-3.5 h-3.5" /></button>
                        <button onClick={() => move(i, 1)} disabled={i === n - 1} className="p-0.5 text-white disabled:opacity-30" title="Move later" data-testid={`album-move-right-${i}`}><ChevronRight className="w-3.5 h-3.5" /></button>
                      </div>
                    </div>
                  ))}
                </div>
                {hasVideos && (
                  <p className="font-mono text-[10px] text-amber-300/90">Video clips play full length and are muted in the album — the music is the soundtrack.</p>
                )}
              </>
            )}
          </CardContent>
        </Card>

        {/* STEP 2 — music (shared card from parent) */}
        {musicSlot}
      </div>

      {n > 0 && (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          {/* STEP 3 — album settings */}
          <Card className="border-border/60">
            <CardContent className="p-4 space-y-4">
              <p className="font-mono text-xs font-bold text-primary">3 · Album settings</p>
              <div>
                <p className="font-mono text-[10px] uppercase text-muted-foreground mb-1.5">Screen size / aspect ratio</p>
                <div className="flex flex-wrap gap-1.5">
                  {ASPECTS.map((a) => (
                    <Button key={a.id} size="sm" variant={settings.aspect === a.id ? "default" : "outline"} className="h-7 font-mono text-[10px]" onClick={() => set("aspect")(a.id)} data-testid={a.testId}>
                      {a.label}
                    </Button>
                  ))}
                </div>
              </div>
              <div>
                <p className="font-mono text-[10px] uppercase text-muted-foreground mb-1.5">Transition between items</p>
                <div className="flex flex-wrap gap-1.5">
                  {TRANSITIONS.map((tr) => (
                    <Button key={tr.id} size="sm" variant={settings.transition === tr.id ? "default" : "outline"} className="h-7 font-mono text-[10px]" onClick={() => set("transition")(tr.id)} data-testid={tr.testId}>
                      {tr.label}
                    </Button>
                  ))}
                </div>
              </div>
              {settings.transition !== "none" && (
                <SliderRow label="Transition length" value={settings.transition_seconds} min={0.2} max={2} step={0.1}
                  onChange={set("transition_seconds")} fmt={(x) => `${x}s`} testId="album-transition-duration" />
              )}
              <SliderRow label="Seconds per photo" value={settings.per_photo_seconds} min={1} max={10} step={0.5}
                onChange={set("per_photo_seconds")} fmt={(x) => `${x}s · total ${fmtTime(totalDur)}`} testId="album-per-photo-slider" />
              {music && (
                <>
                  <SliderRow label="Music volume" value={settings.music_volume} min={0} max={1} step={0.05}
                    onChange={set("music_volume")} fmt={(x) => `${Math.round(x * 100)}%`} testId="album-music-volume" />
                  <div className="grid grid-cols-2 gap-3">
                    <SliderRow label="Fade in" value={settings.fade_in} min={0} max={10} step={0.5} onChange={set("fade_in")} fmt={(x) => `${x}s`} testId="album-fade-in" />
                    <SliderRow label="Fade out" value={settings.fade_out} min={0} max={10} step={0.5} onChange={set("fade_out")} fmt={(x) => `${x}s`} testId="album-fade-out" />
                  </div>
                  <label className="flex items-center gap-2 font-mono text-[11px] text-card-foreground">
                    <Switch checked={settings.loop_music} onCheckedChange={(v) => set("loop_music")(v)} data-testid="album-loop-switch" />
                    Loop music if shorter than the album
                  </label>
                </>
              )}
              <div>
                <label className="block font-mono text-[10px] uppercase text-muted-foreground mb-1">Export size</label>
                <select value={settings.resolution} onChange={(e) => set("resolution")(e.target.value)} className="h-8 px-2 rounded-md border border-border bg-background font-mono text-xs" data-testid="album-resolution-select">
                  <option value="1080">1080p</option>
                  <option value="720">720p</option>
                </select>
              </div>
            </CardContent>
          </Card>

          {/* STEP 4 — preview + export */}
          <Card className="border-border/60">
            <CardContent className="p-4 space-y-3">
              <p className="font-mono text-xs font-bold text-primary">4 · Preview &amp; export</p>
              <div className="relative bg-black rounded-lg overflow-hidden mx-auto" style={{ aspectRatio: aspectCss, height: 260, maxWidth: "100%" }}>
                {prevItem && (
                  prevItem.type === "video"
                    ? <video src={`${API}/funtime/file/${prevItem.file_id}`} muted preload="metadata" className="absolute inset-0 w-full h-full object-contain" data-testid="album-preview-prev-img" />
                    : <img src={`${API}/funtime/file/${prevItem.file_id}`} alt="" className="absolute inset-0 w-full h-full object-contain" data-testid="album-preview-prev-img" />
                )}
                {cur && (
                  cur.type === "video" ? (
                    <video
                      key={cur.file_id}
                      ref={vidRef}
                      src={`${API}/funtime/file/${cur.file_id}`}
                      muted playsInline preload="auto"
                      className="absolute inset-0 w-full h-full object-contain"
                      style={transStyle}
                      data-testid="album-preview-img"
                    />
                  ) : (
                    <img
                      src={`${API}/funtime/file/${cur.file_id}`} alt="preview"
                      className="absolute inset-0 w-full h-full object-contain"
                      style={transStyle}
                      data-testid="album-preview-img"
                    />
                  )
                )}
              </div>
              {musicUrl && <audio ref={musicRef} src={musicUrl} preload="auto" crossOrigin="use-credentials" />}
              <div className="flex items-center gap-3">
                <Button size="sm" variant="outline" className="h-8 font-mono text-xs" onClick={togglePlay} data-testid="album-play-btn">
                  {playing ? <Pause className="w-3.5 h-3.5 mr-1" /> : <Play className="w-3.5 h-3.5 mr-1" />}
                  {playing ? "Pause" : "Play"}
                </Button>
                <span className="font-mono text-[10px] text-muted-foreground" data-testid="album-preview-time">
                  Item {n ? idx + 1 : 0}/{n} · {fmtTime(t)} / {fmtTime(totalDur)}
                </span>
              </div>

              <div className="flex flex-wrap items-end gap-2 pt-1">
                <div className="flex-1 min-w-[140px]">
                  <label className="block font-mono text-[10px] uppercase text-muted-foreground mb-1">Output file name</label>
                  <Input value={outputName} onChange={(e) => setOutputName(e.target.value)} className="h-8 font-mono text-xs" data-testid="album-output-name" />
                </div>
                <Button onClick={startExport} disabled={!canExport} className="btn-3d h-8 font-mono text-xs" data-testid="album-export-btn">
                  {job?.status === "processing" ? <Loader2 className="w-3.5 h-3.5 mr-1 animate-spin" /> : <Images className="w-3.5 h-3.5 mr-1" />}
                  Export album MP4
                </Button>
              </div>
              {job?.status === "processing" && (
                <div className="space-y-1" data-testid="album-export-progress">
                  <div className="flex justify-between font-mono text-[10px]">
                    <span className="text-muted-foreground">Processing… {job.progress}%</span>
                    <button onClick={cancelExport} className="text-rose-400 flex items-center gap-1" data-testid="album-export-cancel"><XCircle className="w-3 h-3" />Cancel</button>
                  </div>
                  <div className="h-2 rounded bg-secondary overflow-hidden"><div className="h-full bg-primary transition-all" style={{ width: `${job.progress}%` }} /></div>
                </div>
              )}
              {job?.status === "done" && (
                <Button onClick={downloadExport} disabled={downloading} className="w-full h-9 bg-emerald-500 text-black hover:bg-emerald-400 font-mono text-xs font-bold" data-testid="album-download-btn">
                  {downloading ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Download className="w-4 h-4 mr-1" />}
                  {downloading ? "Downloading…" : `Download ${outputName || "album"}.mp4`}
                </Button>
              )}
              {job?.status === "error" && (
                <p className="font-mono text-[10px] text-rose-400" data-testid="album-export-error">{job.error || "Export failed."} — adjust and retry.</p>
              )}
            </CardContent>
          </Card>
        </div>
      )}

      <CleanupDialog
        open={cleanupOpen} onClose={() => setCleanupOpen(false)} albumName={name}
        title="Export downloaded — clean up?"
        description="What should happen to this album and its media?"
        onKeep={() => setCleanupOpen(false)}
        onDeleteAlbum={() => cleanupDelete(false)}
        onDeleteAll={() => cleanupDelete(true)}
      />
    </div>
  );
}
